import { Link, useNavigate } from "react-router-dom";
import { Bank, SquaresFour, Briefcase, CurrencyInr, UsersThree, DownloadSimple, SignOut, CaretRight } from "@phosphor-icons/react";
import { toast } from "sonner";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { CURRENCIES } from "../lib/format";

const ITEMS = [
  { to: "/more/accounts", icon: Bank, label: "Accounts & Cards", testId: "more-accounts" },
  { to: "/more/categories", icon: SquaresFour, label: "Categories & Tags", testId: "more-categories" },
  { to: "/more/businesses", icon: Briefcase, label: "Businesses & Projects", testId: "more-businesses" },
  { to: "/more/income-sources", icon: CurrencyInr, label: "Income Sources", testId: "more-income-sources" },
  { to: "/more/members", icon: UsersThree, label: "Household Members", testId: "more-members" },
];

export default function More() {
  const { user, member, household, logout, refresh } = useAuth();
  const navigate = useNavigate();

  const changeCurrency = async (code) => {
    try {
      await api.put("/households/settings", { default_currency: code });
      await refresh();
      toast.success(`Currency set to ${code}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not update currency");
    }
  };

  const exportAll = async () => {
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/export/transactions`, { credentials: "include" });
      if (!res.ok) throw new Error();
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "transactions.csv";
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Export downloaded");
    } catch {
      toast.error("Export failed");
    }
  };

  return (
    <div className="px-5 pt-8" data-testid="more-page">
      <h1 className="text-2xl font-heading font-bold tracking-tight">More</h1>

      <div className="mt-5 bg-white border border-border rounded-2xl p-4 flex items-center gap-3">
        {user?.picture ? (
          <img src={user.picture} alt="" className="h-12 w-12 rounded-full object-cover" />
        ) : (
          <div className="h-12 w-12 rounded-full bg-secondary" />
        )}
        <div className="flex-1 min-w-0">
          <p className="font-medium truncate">{member?.display_name}</p>
          <p className="text-xs text-muted-foreground truncate">{user?.email} · {member?.role} · {household?.name}</p>
        </div>
      </div>

      <div className="mt-4 bg-white border border-border rounded-2xl overflow-hidden">
        {ITEMS.map(({ to, icon: Icon, label, testId }) => (
          <Link key={to} to={to} data-testid={testId} className="flex items-center gap-3 px-4 h-14 border-b border-border last:border-0 active:bg-secondary/50">
            <Icon size={20} className="text-muted-foreground" />
            <span className="flex-1 text-sm font-medium">{label}</span>
            <CaretRight size={16} className="text-muted-foreground" />
          </Link>
        ))}
        <button data-testid="more-export" onClick={exportAll} className="w-full flex items-center gap-3 px-4 h-14 active:bg-secondary/50 text-left">
          <DownloadSimple size={20} className="text-muted-foreground" />
          <span className="flex-1 text-sm font-medium">Export all transactions (CSV)</span>
        </button>
      </div>

      {member?.role === "admin" && (
        <div className="mt-4 bg-white border border-border rounded-2xl p-4 flex items-center justify-between gap-3">
          <div>
            <p className="text-sm font-medium">Household currency</p>
            <p className="text-xs text-muted-foreground">Existing amounts keep their values; only the symbol changes.</p>
          </div>
          <select
            data-testid="currency-select"
            value={household?.default_currency || "INR"}
            onChange={(e) => changeCurrency(e.target.value)}
            className="h-11 px-3 rounded-xl bg-background border border-border text-sm font-medium"
          >
            {CURRENCIES.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
        </div>
      )}

      <button
        data-testid="logout-btn"
        onClick={async () => { await logout(); navigate("/login", { replace: true }); }}
        className="mt-4 w-full h-12 rounded-xl border border-destructive text-destructive font-medium flex items-center justify-center gap-2 active:scale-[0.98] transition-transform"
      >
        <SignOut size={18} /> Sign out
      </button>
      <p className="mt-6 text-center text-xs text-muted-foreground">FamilyLedger · {household?.default_currency || "INR"} · {household?.timezone}</p>
    </div>
  );
}
